import { useEffect, useState } from "react"
import { Pencil, Plus, UserCheck, UserX, X } from "lucide-react"
import Card from "../components/Card"
import StatusPill from "../components/StatusPill"
import InviteUserModal from "../components/InviteUserModal"
import { useAuth } from "../auth/AuthProvider"
import type { Role } from "../types"

type TeamMember = {
  id: string
  name: string
  email: string
  role: string
  status: string
  created_at: string
}

type UsersResponse = {
  users?: TeamMember[]
}

const UsersPage = () => {
  const { hasRole } = useAuth()
  const isAdmin = hasRole(["Admin"])
  const [users, setUsers] = useState<TeamMember[]>([])
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<TeamMember | null>(null)
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "Staff" as Role })

  const fetchUsers = () => {
    fetch("/api/users")
      .then((r) => r.json())
      .then((d: UsersResponse) => { if (d.users) setUsers(d.users) })
      .catch(() => {})
  }

  useEffect(() => { fetchUsers() }, [])

  const handleEdit = (user: TeamMember) => {
    setEditingUser(user)
    setEditForm({ name: user.name, email: user.email, role: user.role as Role })
  }

  const handleSaveEdit = async () => {
    if (!editingUser) return
    try {
      const res = await fetch(`/api/users/${editingUser.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      })
      if (!res.ok) {
        const data = await res.json()
        alert(data.error || "Failed to update user")
        return
      }
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingUser.id ? { ...u, ...editForm } : u
        )
      )
      setEditingUser(null)
    } catch {
      alert("Failed to update user")
    }
  }

  const handleToggleStatus = async (user: TeamMember) => {
    if (user.status === "active") {
      if (!confirm(`Deactivate ${user.name}?`)) return
      try {
        const res = await fetch(`/api/users/${user.id}`, { method: "DELETE" })
        if (!res.ok) {
          const data = await res.json()
          alert(data.error || "Failed to deactivate user")
          return
        }
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, status: "inactive" } : u))
        )
      } catch {
        alert("Failed to deactivate user")
      }
    } else {
      try {
        const res = await fetch(`/api/users/${user.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "active" }),
        })
        if (!res.ok) {
          const data = await res.json()
          alert(data.error || "Failed to reactivate user")
          return
        }
        setUsers((prev) =>
          prev.map((u) => (u.id === user.id ? { ...u, status: "active" } : u))
        )
      } catch {
        alert("Failed to reactivate user")
      }
    }
  }

  const roleTone = (role: string): "success" | "warning" | "neutral" => {
    if (role === "Admin") return "success"
    if (role === "Contractor") return "warning"
    return "neutral"
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-semibold text-slate-900">Users</p>
            <p className="text-sm text-stone">
              Manage team members, roles, and access.
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setIsInviteOpen(true)}
              className="flex items-center gap-2 rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-moss/90"
            >
              <Plus className="h-4 w-4" />
              Invite User
            </button>
          )}
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs font-medium uppercase tracking-wider text-stone">
                <th className="pb-3 pr-4">Name</th>
                <th className="pb-3 pr-4">Email</th>
                <th className="pb-3 pr-4">Role</th>
                <th className="pb-3 pr-4">Status</th>
                <th className="pb-3 pr-4">Joined</th>
                {isAdmin && <th className="pb-3">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {users.map((user) => (
                <tr key={user.id} className="group">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-moss/10 text-xs font-medium text-moss">
                        {user.name?.[0] || "?"}
                      </div>
                      <span className="font-semibold text-slate-900">{user.name}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-stone">{user.email}</td>
                  <td className="py-3 pr-4">
                    <StatusPill tone={roleTone(user.role)} label={user.role} />
                  </td>
                  <td className="py-3 pr-4">
                    <StatusPill
                      tone={user.status === "active" ? "success" : "danger"}
                      label={user.status}
                    />
                  </td>
                  <td className="py-3 pr-4 text-stone">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  {isAdmin && (
                    <td className="py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(user)}
                          className="rounded-lg p-1.5 text-stone transition hover:bg-slate-100 hover:text-moss"
                          title="Edit user"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user)}
                          className="rounded-lg p-1.5 text-stone transition hover:bg-slate-100 hover:text-moss"
                          title={user.status === "active" ? "Deactivate" : "Reactivate"}
                        >
                          {user.status === "active" ? (
                            <UserX className="h-4 w-4" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-[24px] bg-white p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Edit User</h2>
              <button
                onClick={() => setEditingUser(null)}
                className="rounded-full p-2 transition hover:bg-slate-100"
              >
                <X className="h-5 w-5 text-stone" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Role</label>
                <div className="grid grid-cols-3 gap-2">
                  {(["Admin", "Staff", "Contractor"] as Role[]).map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, role })}
                      className={`rounded-xl border px-2 py-2 text-sm font-medium transition ${
                        editForm.role === role
                          ? "border-moss bg-moss text-white"
                          : "border-line bg-white text-stone hover:bg-slate-50"
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setEditingUser(null)}
                  className="flex-1 rounded-full border border-line px-4 py-2.5 text-sm font-semibold text-stone transition hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 rounded-full bg-moss px-4 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-moss/90"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InviteUserModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onSuccess={() => {
          setIsInviteOpen(false)
          fetchUsers()
        }}
      />
    </div>
  )
}

export default UsersPage
